import * as Sentry from '@sentry/nextjs'

import { getPathname, type Locale } from '@/_i18n/routing'

import type { GA4Item } from '@/_utils/analytics/datalayer'

const STORAGE_KEYS = {
	PAY_FORM_HTML: 'payFormHtml',
	ORDER_ULID: 'orderUlid',
	GA4_FAMILY_PURCHASE_PENDING: 'ga4FamilyPurchasePending'
} as const

const ga4FamilyPurchaseOrderKey = (orderUlid: string) => `ga4FamilyPurchase:${orderUlid}`

export type Ga4FamilyPurchaseSnapshot = {
	items: GA4Item[]
	value: number
	coupon: string
}

/**
 * Persists raw payment gateway HTML (e.g. embedded form markup) under a fixed localStorage key.
 *
 * @param html - HTML string returned by the payment provider to render the checkout form.
 * @returns Nothing; failures are reported to Sentry and the console.
 */
export const savePayFormHtml = (html: string): void => {
	try {
		localStorage.setItem(STORAGE_KEYS.PAY_FORM_HTML, html)
	} catch (error) {
		Sentry.captureException(error)
		console.error('Chyba pri ukladaní platobného formulára:', error)
	}
}

/**
 * Stores the current order ULID for use after redirects (e.g. payment return).
 *
 * @param orderUlid - Server-issued order identifier.
 * @returns Nothing; failures are reported to Sentry and the console.
 */
export const saveOrderUlid = (orderUlid: string): void => {
	try {
		localStorage.setItem(STORAGE_KEYS.ORDER_ULID, orderUlid)
	} catch (error) {
		Sentry.captureException(error)
		console.error('Chyba pri ukladaní odkazu na objednávku:', error)
	}
}

export const getPayFormHtml = (): string | null => {
	try {
		return localStorage.getItem(STORAGE_KEYS.PAY_FORM_HTML)
	} catch (error) {
		Sentry.captureException(error)
		console.error('Chyba pri získavaní platobného formulára:', error)
		return null
	}
}

export const clearPayFormHtml = (): void => {
	try {
		localStorage.removeItem(STORAGE_KEYS.PAY_FORM_HTML)
	} catch (error) {
		Sentry.captureException(error)
		console.error('Chyba pri mazaní platobného formulára:', error)
	}
}

export const getOrderUlid = (): string | null => {
	try {
		return localStorage.getItem(STORAGE_KEYS.ORDER_ULID)
	} catch (error) {
		Sentry.captureException(error)
		console.error('Chyba pri získavaní odkazu na objednávku:', error)
		return null
	}
}

export const clearOrderUlid = (): void => {
	try {
		localStorage.removeItem(STORAGE_KEYS.ORDER_ULID)
	} catch (error) {
		Sentry.captureException(error)
		console.error('Chyba pri mazaní odkazu na objednávku:', error)
	}
}

function parseGa4FamilyPurchaseSnapshot(raw: string | null): Ga4FamilyPurchaseSnapshot | null {
	if (!raw) return null
	try {
		const parsed = JSON.parse(raw) as Ga4FamilyPurchaseSnapshot
		if (!Array.isArray(parsed.items) || parsed.items.length === 0) return null
		return parsed
	} catch {
		return null
	}
}

/** Stores family purchase GA4 payload until order ULID is known (after `begin_checkout`). */
export const saveGa4PendingFamilyPurchase = (snapshot: Ga4FamilyPurchaseSnapshot): void => {
	try {
		localStorage.setItem(STORAGE_KEYS.GA4_FAMILY_PURCHASE_PENDING, JSON.stringify(snapshot))
	} catch (error) {
		Sentry.captureException(error)
	}
}

export const clearGa4PendingFamilyPurchase = (): void => {
	try {
		localStorage.removeItem(STORAGE_KEYS.GA4_FAMILY_PURCHASE_PENDING)
	} catch (error) {
		Sentry.captureException(error)
	}
}

/** Moves pending family snapshot to order-scoped key for thank-you `purchase`. */
export const commitGa4FamilyPurchaseSnapshot = (orderUlid: string): void => {
	try {
		const pending = parseGa4FamilyPurchaseSnapshot(
			localStorage.getItem(STORAGE_KEYS.GA4_FAMILY_PURCHASE_PENDING)
		)
		if (!pending) return
		localStorage.setItem(ga4FamilyPurchaseOrderKey(orderUlid), JSON.stringify(pending))
		localStorage.removeItem(STORAGE_KEYS.GA4_FAMILY_PURCHASE_PENDING)
	} catch (error) {
		Sentry.captureException(error)
	}
}

export const getGa4FamilyPurchaseSnapshot = (
	orderUlid: string
): Ga4FamilyPurchaseSnapshot | null => {
	try {
		return parseGa4FamilyPurchaseSnapshot(
			localStorage.getItem(ga4FamilyPurchaseOrderKey(orderUlid))
		)
	} catch (error) {
		Sentry.captureException(error)
		return null
	}
}

export const clearGa4FamilyPurchaseSnapshot = (orderUlid: string): void => {
	try {
		localStorage.removeItem(ga4FamilyPurchaseOrderKey(orderUlid))
	} catch (error) {
		Sentry.captureException(error)
	}
}

/** Decodes HTML entities in a string (e.g. payment gateway markup). */
export const decodeHtml = (html: string): string => {
	const textarea = document.createElement('textarea')
	textarea.innerHTML = html
	return textarea.value
}

/**
 * Builds a locale-aware payment page URL with optional embedded form payload in the query string.
 */
export const createPaymentUrl = (locale: Locale, html: string, orderUlid?: string): string => {
	const base = getPathname({ locale, href: '/order/payment' })
	const encodedHtml = encodeURIComponent(html)
	const orderParam = orderUlid ? `&orderUlid=${encodeURIComponent(orderUlid)}` : ''
	return `${base}?payFormHtml=${encodedHtml}${orderParam}`
}
